import { css } from "@emotion/react";
import { getDefaultStore, useAtomValue } from "jotai";
import { Fragment, type PropsWithChildren, useEffect, useState } from "react";
import { Modal } from "reshaped";

import { useDeviceWidth } from "../../components/adaptation/utilities";
import IconButton from "../../components/Button/IconButton";
import { getSpaceVariable } from "../../components/space/utilities";
import { useTheme } from "../../components/theme/hooks";
import { SafeAreaFrame } from "../SafeArea";
import {
    generalsAtom,
    specialActiveAtom,
    specialHoveredAtom,
    specialsAtom,
    writeAtom,
} from "./constants";
import type { GeneralSupportSection, SpecialSupportSection } from "./types";
const store = getDefaultStore();

const SUPPORT_BAR_WIDTH = 60;
const SUPPORT_PANEL_WIDTH = 400;

export function WithSupportZone(props: PropsWithChildren) {
    const { children } = props;
    const theme = useTheme();
    const size = useDeviceWidth();
    const generals = useAtomValue(generalsAtom);
    const specials = useAtomValue(specialsAtom);

    const width = size === "small" ? SUPPORT_BAR_WIDTH : SUPPORT_PANEL_WIDTH;

    const backgroundCss = css({
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: width,
        background: theme.page_background.highlighted,
        zIndex: 20,
    });

    return (
        <Fragment>
            <div css={backgroundCss}></div>
            {generals ? <GeneralSupportDisplay section={generals} /> : null}
            {Object.values(specials).map((special) => (
                <SpecialSupportDisplay section={special} key={special.id} />
            ))}
            <SafeAreaFrame insets={{ right: width }}>{children}</SafeAreaFrame>
        </Fragment>
    );
}

type SpecialSupportDisplayProps = {
    section: SpecialSupportSection;
};

function SpecialSupportDisplay(props: SpecialSupportDisplayProps) {
    const size = useDeviceWidth();
    if (size === "small") return <SpecialSupportDisplayButton {...props} />;
    return <SpecialSupportDisplayFull {...props} />;
}

function SpecialSupportDisplayButton(props: SpecialSupportDisplayProps) {
    const { section } = props;

    const offset = useAtomValue(section.offset) || 0;
    const height = useAtomValue(section.height) || 0;

    const [opened, setOpened] = useState(false);

    const specialCss = css({
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: SUPPORT_BAR_WIDTH,
        zIndex: 22,
    });

    const markerCss = css({
        position: "absolute",
        top: 0,
        left: 10,
        width: 40,
        background: "lightgrey",
        borderRadius: 10,
        height: height,
    });

    const contentCss = css({
        padding: getSpaceVariable(8),
    });

    return (
        <div css={[specialCss, { top: offset }]}>
            <div css={markerCss} onClick={() => setOpened(true)} />
            <Modal active={opened} onClose={() => setOpened(false)} position="end">
                <div ref={(e) => writeAtom(section.element, e)} css={contentCss} />
            </Modal>
        </div>
    );
}

function SpecialSupportDisplayFull(props: SpecialSupportDisplayProps) {
    const { section } = props;

    const specialHovered = useAtomValue(specialHoveredAtom);
    const element = useAtomValue(section.element);
    const offset = useAtomValue(section.offset) || 0;
    const height = useAtomValue(section.height) || 0;
    const visible = useAtomValue(section.visible);

    const isDisplayed = visible || specialHovered === section;

    const specialCss = css({
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: SUPPORT_PANEL_WIDTH,
        zIndex: 22,
        overflowX: "hidden",
        transition: "all 0.1s ease-in-out",
    });

    const specialHiddenCss = css({
        pointerEvents: "none",
        opacity: 0,
        transform: "translateX(-5px)",
    });

    const markerCss = css({
        position: "absolute",
        top: 0,
        left: 0,
        width: 5,
        background: "red",
        height: height,
    });

    const contentCss = css({
        padding: getSpaceVariable(8),
    });

    useEffect(() => {
        if (!element) return;
        const onMouseEnter = () => writeAtom(specialHoveredAtom, section);
        const onMouseLeave = () => writeAtom(specialHoveredAtom, null);
        element.addEventListener("mouseenter", onMouseEnter);
        element.addEventListener("mouseleave", onMouseLeave);
        return () => {
            element.removeEventListener("mouseenter", onMouseEnter);
            element.removeEventListener("mouseleave", onMouseLeave);
        };
    }, [element, section]);

    return (
        <div css={[specialCss, isDisplayed ? undefined : specialHiddenCss, { top: offset }]}>
            <div css={[{ position: "relative" }]}>
                <div css={markerCss} />
                <div ref={(e) => writeAtom(section.element, e)} css={contentCss} />
            </div>
        </div>
    );
}

type GeneralSupportDisplayProps = {
    section: GeneralSupportSection;
};

function GeneralSupportDisplay(props: GeneralSupportDisplayProps) {
    const size = useDeviceWidth();
    if (size === "small") return <GeneralSupportDisplayButton {...props} />;
    return <GeneralSupportDisplayFull {...props} />;
}

function GeneralSupportDisplayButton(props: GeneralSupportDisplayProps) {
    const { section } = props;
    const containerCss = css({
        position: "fixed",
        top: 0,
        right: 0,
        zIndex: 25,
        height: 60,
        width: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    });

    const [generalActive, setGeneralActive] = useState(false);

    return (
        <div css={containerCss}>
            <IconButton icon="help" action={() => setGeneralActive(true)} role="minor" size="big" />
            <Modal active={generalActive} onClose={() => setGeneralActive(false)} position="end">
                <div ref={(e) => writeAtom(section.element, e)} />
            </Modal>
        </div>
    );
}

function GeneralSupportDisplayFull(props: GeneralSupportDisplayProps) {
    const { section } = props;

    const specialActive = useAtomValue(specialActiveAtom);
    const specialHovered = useAtomValue(specialHoveredAtom);

    const displayed = !specialActive && specialHovered === null;

    const generalCss = css({
        position: "fixed",
        top: 0,
        right: 0,
        bottom: 0,
        width: SUPPORT_PANEL_WIDTH,
        zIndex: 21,
        overflow: "auto",
        overflowX: "hidden",
        padding: getSpaceVariable(8),
        transition: "all 0.1s ease-in-out",
    });

    const generalHiddenCss = css({
        pointerEvents: "none",
        opacity: 0,
    });

    return (
        <div css={[generalCss, displayed ? undefined : generalHiddenCss]}>
            <div ref={(e) => writeAtom(section.element, e)} />
        </div>
    );
}
